import * as React from 'react';
import { router, usePathname } from 'expo-router';
import { useFeatureTours } from '../hooks/use-feature-tours';
import { useDigest } from '../hooks/use-digest';
import { useContextualTour } from '../hooks/use-contextual-tour';
import { TourDigestBanner } from './tour-digest-banner';
import { TourDigestModal } from './tour-digest-modal';
import { ContextualTourPrompt } from './contextual-tour-prompt';
import { TourRenderer } from './tour-renderer';
import type { EligibleTour } from '../types';

const NAVIGATION_SETTLE_MS = 400;

function FeatureTourLayer() {
  const pathname = usePathname();
  const { status, digestTours, contextualTours, startTour, dismissTour, completeTour } =
    useFeatureTours();

  const [isDigestModalVisible, setIsDigestModalVisible] = React.useState(false);
  const [activeTour, setActiveTour] = React.useState<EligibleTour | null>(null);
  const [activeTourStep, setActiveTourStep] = React.useState(0);

  const digest = useDigest(digestTours);

  const activateTour = React.useCallback(
    (tour: EligibleTour) => {
      const needsNavigation =
        tour.contextRoute != null && pathname !== tour.contextRoute;

      if (needsNavigation) {
        router.push(tour.contextRoute as any);
        setTimeout(() => {
          setActiveTour(tour);
          setActiveTourStep(0);
          startTour(tour.slug, tour.version);
        }, NAVIGATION_SETTLE_MS);
      } else {
        setActiveTour(tour);
        setActiveTourStep(0);
        startTour(tour.slug, tour.version);
      }
    },
    [pathname, startTour],
  );

  const handleStartTour = React.useCallback(
    (slug: string) => {
      const tour =
        digestTours.find((t) => t.slug === slug) ??
        contextualTours.find((t) => t.slug === slug) ??
        null;
      if (!tour) return;
      activateTour(tour);
    },
    [digestTours, contextualTours, activateTour]
  );

  const handleDismissTour = React.useCallback(
    (slug: string) => {
      dismissTour(slug);
    },
    [dismissTour]
  );

  const contextual = useContextualTour(contextualTours, pathname, handleStartTour, handleDismissTour);

  if (status === 'loading' || status === 'error') {
    return null;
  }

  function handleDigestBannerPress() {
    setIsDigestModalVisible(true);
    digest.showDigestModal();
  }

  function handleDigestModalClose() {
    setIsDigestModalVisible(false);
    digest.dismissDigest();
  }

  function handleTakeTour(tour: EligibleTour) {
    setIsDigestModalVisible(false);
    activateTour(tour);
  }

  function handleSkipTour(tour: EligibleTour) {
    dismissTour(tour.slug);
  }

  function handleAdvanceStep() {
    setActiveTourStep((prev) => prev + 1);
  }

  function handleCompleteTour() {
    if (activeTour) {
      completeTour(activeTour.slug);
    }
    setActiveTour(null);
    setActiveTourStep(0);
  }

  function handleDismissActiveTour() {
    if (activeTour) {
      dismissTour(activeTour.slug);
    }
    setActiveTour(null);
    setActiveTourStep(0);
  }

  return (
    <>
      <TourDigestBanner
        visible={digest.isVisible && !isDigestModalVisible}
        onPress={handleDigestBannerPress}
      />

      <TourDigestModal
        visible={isDigestModalVisible}
        tours={digest.digestTours}
        onTakeTour={handleTakeTour}
        onSkipTour={handleSkipTour}
        onClose={handleDigestModalClose}
      />

      <ContextualTourPrompt
        visible={contextual.isPromptVisible && activeTour === null}
        tour={contextual.promptTour}
        onAccept={contextual.acceptPrompt}
        onDecline={contextual.declinePrompt}
      />

      {activeTour !== null && (
        <TourRenderer
          tour={activeTour}
          currentStep={activeTourStep}
          onAdvanceStep={handleAdvanceStep}
          onComplete={handleCompleteTour}
          onDismiss={handleDismissActiveTour}
        />
      )}
    </>
  );
}

export { FeatureTourLayer };
